import { isMock, db, storage } from './firebase'
import { logger } from './logger'
import { 
  collection, 
  doc, 
  getDocs,
  addDoc,
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  DocumentData,
  Timestamp
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, type UploadResult } from 'firebase/storage'
import { optimizeImage } from './imageOptimizer'
import type { HomepageSlide, HomepageAbout } from '../types/homepage'

// Firestore collections
const HOMEPAGE_SLIDES_COLLECTION = 'homepageSlides'
const HOMEPAGE_ABOUT_COLLECTION = 'homepageAbout'
const IMAGES_PATH = 'homepage-images'

// Helper function to convert Firestore data to HomepageSlide
const firestoreToSlide = (docId: string, data: DocumentData): HomepageSlide => {
  const convertTimestamp = (timestamp: unknown) => 
    timestamp instanceof Timestamp
      ? timestamp.toDate() 
      : new Date()

  return {
    id: docId,
    imageUrl: data.imageUrl || '',
    title: data.title || '',
    description: data.description || '',
    ctaText: data.ctaText || '',
    ctaLink: data.ctaLink || '',
    order: data.order || 0,
    active: data.active ?? true,
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
  }
}

// Helper function to convert Firestore data to HomepageAbout
const firestoreToAbout = (docId: string, data: DocumentData): HomepageAbout => {
  const convertTimestamp = (timestamp: unknown) => 
    timestamp instanceof Timestamp
      ? timestamp.toDate() 
      : new Date()

  return {
    id: docId,
    imageUrl: data.imageUrl || '',
    title: data.title || '',
    description: data.description || '',
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
  }
}

// Mock data for development
const generateMockSlides = (): HomepageSlide[] => {
  return [
    {
      id: 'slide-1',
      imageUrl: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
      title: 'Conectando Brasileiros nos EUA',
      description: 'Mais de 1 milhão de pessoas já foram ajudadas pelo Grupo Noé em sua jornada no exterior.',
      ctaText: 'Saiba mais',
      ctaLink: '/about',
      order: 0,
      active: true,
      createdAt: new Date('2026-04-01'),
      updatedAt: new Date('2026-04-01'),
    },
    {
      id: 'slide-2',
      imageUrl: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
      title: 'Comunidade que Acolhe',
      description: 'Oferecemos suporte, orientação e oportunidades para brasileiros que buscam uma nova vida nos Estados Unidos.',
      ctaText: 'Junte-se a nós',
      ctaLink: '/register',
      order: 1,
      active: true,
      createdAt: new Date('2026-04-01'),
      updatedAt: new Date('2026-04-01'),
    },
    {
      id: 'slide-3',
      imageUrl: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
      title: 'Tempo de Experiência',
      description: 'Há mais de 10 anos ajudando pessoas a se estabelecerem, encontrar emprego, moradia e construir redes de contato.',
      ctaText: 'Nossa história',
      ctaLink: '/about',
      order: 2,
      active: true,
      createdAt: new Date('2026-04-01'),
      updatedAt: new Date('2026-04-01'),
    },
    {
      id: 'slide-4',
      imageUrl: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
      title: 'Marketplace de Oportunidades',
      description: 'Encontre imóveis, carros, serviços e muito mais em nossa plataforma exclusiva para a comunidade brasileira.',
      ctaText: 'Explorar anúncios',
      ctaLink: '/listings',
      order: 3,
      active: true,
      createdAt: new Date('2026-04-01'),
      updatedAt: new Date('2026-04-01'),
    },
  ]
}

const generateMockAbout = (): HomepageAbout => {
  return {
    id: 'about-1',
    imageUrl: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80',
    title: 'Sobre o Grupo Noé',
    description: 'Mais do que uma comunidade, uma família no exterior',
    createdAt: new Date('2026-04-01'),
    updatedAt: new Date('2026-04-01'),
  }
}

// Service functions
export const homepageService = {
  // Get all slides
  async getSlides(): Promise<HomepageSlide[]> {
    logger.log('getSlides called, isMock:', isMock)
    if (isMock) {
      return generateMockSlides().sort((a, b) => a.order - b.order)
    }

    try {
      const slidesRef = collection(db, HOMEPAGE_SLIDES_COLLECTION)
      const q = query(slidesRef, orderBy('order'))
      const querySnapshot = await getDocs(q)
      
      const slides: HomepageSlide[] = []
      querySnapshot.forEach((doc) => {
        slides.push(firestoreToSlide(doc.id, doc.data()))
      })
      
      // Fallback to mock if no slides found
      if (slides.length === 0) {
        logger.log('No slides found in Firestore, using mock data')
        return generateMockSlides().sort((a, b) => a.order - b.order)
      }
      
      return slides
    } catch (error) {
      logger.error('Error fetching slides from Firestore:', error)
      
      // Check if it's a permission error
      const errorMessage = String(error).toLowerCase()
      const isPermissionError = errorMessage.includes('permission') || 
                               errorMessage.includes('missing or insufficient') ||
                               (error && typeof error === 'object' && 'code' in error && 
                                (error.code === 'permission-denied' || error.code === 'PERMISSION_DENIED'))
      
      if (isPermissionError) {
        logger.warn('Firestore permission denied for slides. Using mock data as fallback.')
        return generateMockSlides().sort((a, b) => a.order - b.order)
      }
      
      return []
    }
  },

  // Get about image
  async getAbout(): Promise<HomepageAbout | null> {
    logger.log('getAbout called, isMock:', isMock)
    if (isMock) {
      return generateMockAbout()
    }

    try {
      const aboutRef = collection(db, HOMEPAGE_ABOUT_COLLECTION)
      const querySnapshot = await getDocs(aboutRef)
      
      if (querySnapshot.empty) {
        logger.log('No about data found in Firestore, using mock data')
        return generateMockAbout()
      }
      
      // Get the first document (assuming only one about config)
      const doc = querySnapshot.docs[0]
      return firestoreToAbout(doc.id, doc.data())
    } catch (error) {
      logger.error('Error fetching about from Firestore:', error)
      
      // Check if it's a permission error
      const errorMessage = String(error).toLowerCase()
      const isPermissionError = errorMessage.includes('permission') || 
                               errorMessage.includes('missing or insufficient') ||
                               (error && typeof error === 'object' && 'code' in error && 
                                (error.code === 'permission-denied' || error.code === 'PERMISSION_DENIED'))
      
      if (isPermissionError) {
        logger.warn('Firestore permission denied for about. Using mock data as fallback.')
        return generateMockAbout()
      }
      
      return null
    }
  },

  // Create a new slide
  async createSlide(slideData: Omit<HomepageSlide, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    logger.log('createSlide called with data:', slideData)
    
    if (isMock) {
      const mockId = `slide-${Date.now()}`
      logger.log('Mock mode: created slide with ID', mockId)
      return mockId
    }

    try {
      const slideRef = await addDoc(collection(db, HOMEPAGE_SLIDES_COLLECTION), {
        ...slideData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      
      logger.log('Slide created in Firestore with ID:', slideRef.id)
      return slideRef.id
    } catch (error) {
      logger.error('Error creating slide in Firestore:', error)
      throw error
    }
  },

  // Update an existing slide
  async updateSlide(id: string, slideData: Partial<HomepageSlide>): Promise<void> {
    logger.log('updateSlide called for ID:', id, 'data:', slideData)
    
    if (isMock) {
      logger.log('Mock mode: updated slide', id)
      return
    }

    try {
      const slideRef = doc(db, HOMEPAGE_SLIDES_COLLECTION, id)
      await updateDoc(slideRef, {
        ...slideData,
        updatedAt: serverTimestamp(),
      })
      
      logger.log('Slide updated in Firestore:', id)
    } catch (error) {
      logger.error('Error updating slide in Firestore:', error)
      throw error
    }
  },

  // Delete a slide
  async deleteSlide(id: string): Promise<void> {
    logger.log('deleteSlide called for ID:', id)
    
    if (isMock) {
      logger.log('Mock mode: deleted slide', id)
      return
    }

    try {
      const slideRef = doc(db, HOMEPAGE_SLIDES_COLLECTION, id)
      await deleteDoc(slideRef)
      
      logger.log('Slide deleted from Firestore:', id)
    } catch (error) {
      logger.error('Error deleting slide from Firestore:', error)
      throw error
    }
  },

  // Update about image
  async updateAbout(aboutData: Partial<HomepageAbout>): Promise<void> {
    logger.log('updateAbout called with data:', aboutData)
    
    if (isMock) {
      logger.log('Mock mode: updated about')
      return
    }

    try {
      const aboutRef = collection(db, HOMEPAGE_ABOUT_COLLECTION)
      const querySnapshot = await getDocs(aboutRef)
      
      if (querySnapshot.empty) {
        // Create new document if none exists
        await addDoc(aboutRef, {
          ...aboutData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
      } else {
        // Update existing document
        const docId = querySnapshot.docs[0].id
        const docRef = doc(db, HOMEPAGE_ABOUT_COLLECTION, docId)
        await updateDoc(docRef, {
          ...aboutData,
          updatedAt: serverTimestamp(),
        })
      }
      
      logger.log('About updated in Firestore')
    } catch (error) {
      logger.error('Error updating about in Firestore:', error)
      throw error
    }
  },

  // Upload image for slide or about
  async uploadImage(file: File, type: 'slide' | 'about' = 'slide'): Promise<string> {
    logger.log('uploadImage called for type:', type, 'file:', file.name)
    
    if (isMock) {
      // Return a mock URL for development
      const mockUrl = `https://images.unsplash.com/photo-${Date.now()}?w=1920&q=80`
      logger.log('Mock mode: returning mock URL', mockUrl)
      return mockUrl
    }

    try {
      // Optimize image before upload
      const optimizedFile = await optimizeImage(file, {
        maxWidth: type === 'slide' ? 1920 : 1200,
        maxHeight: type === 'slide' ? 1080 : 800,
        quality: 0.8,
      })

      // Generate unique filename
      const timestamp = Date.now()
      const randomString = Math.random().toString(36).substring(2, 15)
      const fileName = `${type}-${timestamp}-${randomString}.${optimizedFile.type.split('/')[1] || 'jpg'}`
      
      // Create storage reference
      const storageRef = ref(storage, `${IMAGES_PATH}/${fileName}`)
      
      // Upload file
      logger.log('Uploading image to storage:', fileName)
      const uploadResult: UploadResult = await uploadBytes(storageRef, optimizedFile)
      logger.log('Upload completed:', uploadResult.metadata.fullPath)
      
      // Get download URL
      const downloadUrl = await getDownloadURL(uploadResult.ref)
      logger.log('Download URL obtained:', downloadUrl)
      
      return downloadUrl
    } catch (error) {
      logger.error('Error uploading image:', error)
      throw error
    }
  },
}